import React, { useState, useEffect, useRef, useCallback } from 'react';
import useDebounce from 'hooks/use-debounce';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import SuggestedLocationDropdown from 'components/suggested-location-dropdown';
import { query, getDirectionAndAddOverlays } from 'utils/apple-maps';
import { pluralizeWithCount } from '@zoocasa/node-kit/strings/pluralize';
import TextInput from '@zoocasa/node-kit/components/controls/text-input';
import Button from 'components/control/button';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import AppleMap from '@zoocasa/node-kit/map/apple/map';
import BaseMap from 'components/map/base-map';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useTranslation } from 'next-i18next';

import type SearchPrediction from 'data/search-predictions';
import type { Point } from 'data/listing';

//#region types
export type StartingAddress = {
  id: number;
  streetAddress: string;
  city: string;
  province: string;
  position: Point;
};

type TravelTimeProps = {
  address: StartingAddress;
};
//#endregion

//#region component
export default function TravelTime({ address }: TravelTimeProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const mapRef = useRef<AppleMap | null>(null);
  const [destinationName, setDestinationName] = useState('');
  const [isDriving, setIsDriving] = useState(true);
  const [isLoadingSearchPredictions, setIsLoadingSearchPredictions] = useState(false);
  const [locationPredictions, setLocationPredictions] = useState<SearchPrediction[]>([]);
  const [isPredictionDropdownOpen, setIsPredictionDropdownOpen] = useState(false);
  const [distance, setDistance] = useState<number|undefined>();
  const [travelTime, setTravelTime] = useState<number|undefined>();

  const { id, position, streetAddress, city, province } = address;
  const listingAddress = `${streetAddress}, ${city}, ${province}`;
  const mapElementId = `${id}-travel-time`;

  const closeSearchDropdown = () => setIsPredictionDropdownOpen(false);
  const debouncedSearch = useDebounce(() => runLocationSearch(), 400);
  const handleMapLoaded = useCallback((mapInstance: AppleMap) => {
    mapRef.current = mapInstance;
  }, []);
  const getDirections = useCallback(async (address?: string) => {
    if (destinationName.length && mapRef.current) {
      try {
        const directionInformation: Record<string, number> = await getDirectionAndAddOverlays(mapRef.current, address || destinationName, listingAddress, isDriving);
        setDistance(directionInformation.distance);
        setTravelTime(directionInformation.expectedTravelTime);
      } catch (error) {
        console.error('Error getting directions:', error);
        setDistance(0);
        setTravelTime(0);
      }
    }
  }, [destinationName, listingAddress, isDriving]);

  useEffect(() => {
    getDirections();
  }, [isDriving, getDirections]);

  const runLocationSearch = async () => {
    setIsLoadingSearchPredictions(true);
    try {
      const searchResults = await query(destinationName, true);
      setLocationPredictions(searchResults);
      setIsPredictionDropdownOpen(true);
    } catch (error) {
      console.error('Error searching locations:', error);
      setLocationPredictions([]);
    } finally {
      setIsLoadingSearchPredictions(false);
    }
  };

  const handleInput = (value: string) => {
    setDestinationName(value);
    if (value.trim()) {
      debouncedSearch();
    } else {
      setLocationPredictions([]);
      setIsPredictionDropdownOpen(false);
    }
  };

  const handleLocationClick = async (searchPrediction: SearchPrediction) => {
    const destinationAddress = searchPrediction.description;
    setDestinationName(destinationAddress);
    getDirections(destinationAddress);
    setIsPredictionDropdownOpen(false);
  };

  return (
    <div className={styles.component}>
      <div className={styles['input-row']}>
        <div className={styles.direction}>
          <span/>
          <span/>
          <span/>
          <span/>
          <span/>
        </div>
        <section className={styles['input-container']}>
          <div className={styles.destination}>
            {t(LOCALE_COMMON_PATHS.ADDRESS_TRAVEL_TIME_STARTING_ADDRESS)}
            <TextInput value={streetAddress || ''} disabled={true} />
          </div>
          <div ref={useOutsideClickHandler(closeSearchDropdown)}>
            {t(LOCALE_COMMON_PATHS.ADDRESS_TRAVEL_TIME_DESTINATION_ADDRESS)}
            <div>
              <TextInput
                value={destinationName}
                placeholder={t(LOCALE_COMMON_PATHS.ADDRESS_TRAVEL_TIME_ENTER_ADDRESS_PLACEHOLDER)}
                onValueChange={handleInput}
              />
              <SuggestedLocationDropdown
                isLoading={isLoadingSearchPredictions}
                searchPredictions={locationPredictions}
                setActivePrediction={() => { /* no-op */ }}
                onClick={handleLocationClick}
                isActive={isPredictionDropdownOpen}
                className={styles['suggested-location-dropdown']}
                searchAgentPredictions={[]}
              />
            </div>
          </div>
        </section>
      </div>
      <section className={styles['button-info-container']}>
        <div className={styles.tabs}>
          <Button
            label={t(LOCALE_COMMON_PATHS.ADDRESS_TRAVEL_TIME_DRIVING)}
            className={buildClassName(isDriving && styles.active)}
            onClick={() => setIsDriving(true)}
          />
          <Button
            label={t(LOCALE_COMMON_PATHS.ADDRESS_TRAVEL_TIME_WALKING)}
            className={buildClassName(!isDriving && styles.active)}
            onClick={() => setIsDriving(false)}
          />
        </div>
        {!!distance && distance !== 0 && travelTime && travelTime !== 0 &&
          <div className={styles['trip-info']}>
            <div className={styles['trip-info-title']}>
              {isDriving ? t(LOCALE_COMMON_PATHS.ADDRESS_TRAVEL_TIME_DRIVING) : t(LOCALE_COMMON_PATHS.ADDRESS_TRAVEL_TIME_WALKING)}
            </div>
            <div className={styles['trip-info-stats']}>
              <div>
                {secondsFormatter(travelTime)}
              </div>
              <div className={styles['trip-info-kms']}>
                {Math.round(distance / 1000)}km
              </div>
            </div>
          </div>
        }
        {distance === 0 && travelTime === 0 && <div>{t(LOCALE_COMMON_PATHS.ADDRESS_TRAVEL_TIME_NO_RESULTS)}</div>}
      </section>
      <BaseMap
        position={position}
        mapElementId={mapElementId}
        isInteractive={false}
        renderPin={true}
        shouldLoad={true}
        address={listingAddress}
        className={styles.map}
        onMapLoaded={handleMapLoaded}
        testId='travel-time-map'
      />
    </div>
  );
}
//#endregion

//#region helpers
function secondsFormatter(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - (hours * 3600)) / 60);
  return `${pluralizeTime(hours, 'Hour')} ${pluralizeTime(minutes, 'Minute')}`;
}

function pluralizeTime(time: number, unit: string) {
  return time === 0 ? '' : pluralizeWithCount(time, unit);
}
//#endregion